<div class="flex flex-col items-center justify-center p-8 font-sans">

  <div class="text-center mb-10">
    <h1 class="text-3xl font-bold text-gray-800 tracking-tight">
      Image to Video
    </h1>
    <p class="mt-2 text-gray-500">
      Upload an image and we'll turn it into a video using AI.
    </p>
  </div>

  <div class="w-full max-w-2xl bg-white border border-gray-200 rounded-2xl p-10 flex flex-col gap-8 shadow-sm">

    <div class="flex items-center gap-4">
      <div class="w-11 h-11 bg-violet-50 border border-violet-200 rounded-xl flex items-center justify-center text-violet-500 shrink-0">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <polygon points="23 7 16 12 23 17 23 7"/>
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
        </svg>
      </div>
      <div>
        <h1 class="text-gray-800 text-lg font-semibold tracking-tight m-0">Image to Video</h1>
        <p class="text-gray-400 text-xs mt-0.5 m-0">Upload an image and we'll generate a video from it</p>
      </div>
    </div>

    <ng-container *ngIf="step === 'upload'">

      <div
        (dragover)="onDragOver($event)"
        (dragleave)="isDragging = false"
        (drop)="onDrop($event)"
        (click)="fileInput.click()"
        [class]="'relative h-60 rounded-2xl flex items-center justify-center cursor-pointer overflow-hidden transition-all duration-200 '
          + (isDragging ? 'border-2 border-violet-400 bg-violet-50 border-solid'
          : previewUrl  ? 'border border-gray-200 border-solid'
                        : 'border-2 border-dashed border-gray-200 hover:border-violet-400 hover:bg-violet-50')"
      >
        <img
          *ngIf="previewUrl"
          [src]="previewUrl"
          alt="Preview"
          class="w-full h-full object-cover"
        />

        <div *ngIf="!previewUrl" class="flex flex-col items-center gap-2 text-gray-400 pointer-events-none">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" class="opacity-50">
            <rect x="3" y="3" width="18" height="18" rx="3"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          <span class="text-sm font-medium text-gray-600">Drop your image here</span>
          <small class="text-xs">JPG, PNG, WEBP · Max 5MB</small>
        </div>

        <div
          *ngIf="previewUrl"
          class="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-sm font-medium opacity-0 hover:opacity-100 transition-opacity duration-200"
        >
          Click to change
        </div>
      </div>

      <input
        #fileInput
        type="file"
        accept="image/jpg,image/jpeg,image/png,image/webp"
        (change)="onFileSelected($event)"
        class="hidden"
      />

      <div *ngIf="errorMessage" class="flex items-center gap-1.5 text-red-400 text-xs -mt-4">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        {{ errorMessage }}
      </div>

      <button
        [disabled]="!selectedFile"
        (click)="upload()"
        class="w-full flex items-center justify-center gap-2 py-3.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-30 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition-all duration-200 hover:-translate-y-px"
      >
        Generate Video
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="5" y1="12" x2="19" y2="12"/>
          <polyline points="12 5 19 12 12 19"/>
        </svg>
      </button>

    </ng-container>

    <ng-container *ngIf="step === 'processing'">
      <div class="flex flex-col items-center gap-5 py-8">

        <div class="w-full h-0.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            class="h-full bg-violet-500 rounded-full transition-all duration-700 ease-out"
            [style.width.%]="progressValue"
          ></div>
        </div>

        <p class="text-gray-400 text-sm m-0">{{ processingLabel }}</p>

        <div class="flex gap-1.5">
          <span class="w-1.5 h-1.5 rounded-full bg-violet-500 animate-bounce [animation-delay:0ms]"></span>
          <span class="w-1.5 h-1.5 rounded-full bg-violet-500 animate-bounce [animation-delay:150ms]"></span>
          <span class="w-1.5 h-1.5 rounded-full bg-violet-500 animate-bounce [animation-delay:300ms]"></span>
        </div>

      </div>
    </ng-container>

    <ng-container *ngIf="step === 'result'">

      <div *ngIf="videoUrl" class="flex flex-col gap-5">
        <span class="inline-flex items-center gap-1.5 self-start bg-emerald-50 border border-emerald-200 text-emerald-600 text-xs font-semibold px-3 py-1 rounded-full tracking-wide">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Done
        </span>

        <video
          [src]="videoUrl"
          controls
          autoplay
          loop
          class="w-full rounded-xl border border-gray-200 bg-black"
        ></video>

        <div class="flex items-center justify-between gap-4 flex-wrap">
          <a [href]="videoUrl"
            target="_blank"
            class="font-mono text-xs text-violet-500 hover:underline truncate max-w-xs"
          >
            {{ videoUrl }}
          </a>
          <button
            (click)="reset()"
            class="text-sm text-gray-600 border border-gray-200 hover:border-violet-400 rounded-xl px-4 py-2 transition-colors duration-200 bg-transparent cursor-pointer"
          >
            Generate another
          </button>
        </div>
      </div>

      <div *ngIf="!videoUrl && errorMessage" class="flex flex-col items-center gap-4 text-red-400 text-sm text-center">
        <p class="m-0">{{ errorMessage }}</p>
        <button
          (click)="reset()"
          class="text-sm text-gray-600 border border-gray-200 hover:border-violet-400 rounded-xl px-4 py-2 transition-colors duration-200 bg-transparent cursor-pointer"
        >
          Try again
        </button>
      </div>

    </ng-container>

  </div>
</div>
